import React, { memo, useCallback, useMemo, useState } from 'react';

import { Confirm, Modal } from '@components/common';
import Table from '@components/common/Table/Table';
import BrandingSetupForm from '@components/ModalComponents/BrandingSetupForm';

import { Icons } from '@icons/icons';

import { useMutateRemoveBrandingSetups } from '@hooks/organisation/useMutateRemoveBrandingSetups';
import { useMutateRemoveSingleBrandingSetup } from '@hooks/organisation/useMutateRemoveSingleBrandingSetup';
import { useQueryBrandingSetups } from '@hooks/organisation/useQueryBrandingSetups';

import { organisationBranding } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import { setPage, setPageSize, setSearchValue, setSort } from '@slices/organisationBranding';

import { ISortBy, ITablePagination, ITableSearch, ITableSort } from '@interfaces/components/table';
import { IBrandingSetup } from '@interfaces/platform/branding';

import { MODAL_WIDTH_480, modalCloseConfirm } from '@constants/modals';
import { searchTooltipText } from './constants';

import { getColumns } from './managePlatformBrandingColumns';

import styles from './ManagePlatformBranding.module.scss';

const ManagePlatformBranding = () => {
	const dispatch = useAppDispatch();

	const [isBrandingSetupModalOpen, setIsBrandingSetupModalOpen] = useState(false);
	const [isEditMode, setIsEditMode] = useState(false);
	const [editableSetup, setEditableSetup] = useState<IBrandingSetup | null>(null);
	const [checkedIds, setCheckedIds] = useState<Array<string>>([]);
	const [singleIdForRemove, setSingleIdForRemove] = useState<string | null>(null);
	const [isShowDeleteConfirm, setIsShowDeleteConfirm] = useState(false);
	const [isDirty, setIsDirty] = useState(false);
	const [isCloseConfirmModal, setIsCloseConfirmModal] = useState(false);

	const toggleCreateSetupModalModal = () => setIsBrandingSetupModalOpen(prevState => !prevState);

	const openEditModal = () => {
		setIsEditMode(true);
		setIsBrandingSetupModalOpen(true);
	};

	const closeModals = useCallback(() => {
		setIsBrandingSetupModalOpen(false);
		setIsEditMode(false);
		setIsCloseConfirmModal(false);
		setEditableSetup(null);
	}, []);

	const toggleBrandingSetupModal = useCallback(() => {
		if (isDirty) setIsCloseConfirmModal(prevState => !prevState);
		else closeModals();
	}, [isDirty, closeModals]);

	const openDeleteConfirm = () => setIsShowDeleteConfirm(true);

	const closeDeleteConfirm = useCallback(() => {
		setIsShowDeleteConfirm(false);

		setSingleIdForRemove(null);
	}, []);

	const {
		page,
		pageSize,
		searchValue,
		sortFieldName,
		sortDirection,
	} = useAppSelector(organisationBranding);

	const {
		brandingSetups,
		countOfRecords,
		isLoadingBrandingSetups,
	} = useQueryBrandingSetups();

	const {
		handleDeleteBrandingSetup,
		isLoadingDeleteBrandingSetup,
	} = useMutateRemoveSingleBrandingSetup([closeDeleteConfirm]);
	const {
		handleDeleteBrandingSetups,
		isLoadingDeleteBrandingSetups,
	} = useMutateRemoveBrandingSetups([closeDeleteConfirm]);

	const deleteItemsHandler = useCallback(() => {
		if (singleIdForRemove) {
			handleDeleteBrandingSetup(singleIdForRemove);
		} else {
			handleDeleteBrandingSetups(checkedIds as any);
		}
	}, [
		singleIdForRemove,
		checkedIds,
		handleDeleteBrandingSetup,
		handleDeleteBrandingSetups,
	]);

	const columns = useMemo(() => getColumns({
		openEditModal,
		setEditableSetup,
		setSingleIdForRemove,
		openDeleteConfirm,
	}), []);

	const onSearchHandler = useCallback((searchValue: string) => dispatch(setSearchValue({ searchValue })), [dispatch]);

	const search: Array<ITableSearch> = useMemo(() => [
		{
			initialValue: searchValue,
			name: 'searchValue',
			handler: onSearchHandler,
			tooltipText: searchTooltipText,
			placeholder: searchTooltipText,
		},
	], [searchValue, onSearchHandler]);

	const pageHandler = useCallback((page: number) => dispatch(setPage({ page })), [dispatch]);

	const pageSizeHandler = useCallback((pageSize: number) => dispatch(setPageSize({ pageSize })), [dispatch]);

	const pagination: ITablePagination = useMemo(() => (
		{
			countOfRecords,
			forcePage: page,
			size: pageSize,
			pageHandler,
			pageSizeHandler,
		}
	), [
		countOfRecords,
		page,
		pageSize,
		pageHandler,
		pageSizeHandler,
	]);

	const handleSort = useCallback((sortBy: Array<ISortBy>) => {
		dispatch(setSort({
			sortDirection: !!sortBy[0]?.desc,
			sortFieldName: sortBy[0]?.id,
		}));
	}, [dispatch]);

	const sort: ITableSort = useMemo(() => (
		{
			initialState: [{ id: sortFieldName, desc: sortDirection }],
			handleSort,
		}
	), [sortFieldName, sortDirection, handleSort]);

	return (
		<div className={styles.managePlatformBrandingContainer}>
			<div className={styles.pageTitle}>
				<div className={styles.icon}>{Icons.bag({ isActive: true })}</div>
				<div className={styles.title}>Manage Platform Branding</div>
			</div>
			<div className={styles.platformBrandingTableContainer}>
				<Table
					columns={columns}
					data={brandingSetups}
					isCreatable
					isLoading={isLoadingBrandingSetups}
					isOpenSearch={!!searchValue.length}
					isWithoutFilter
					pagination={pagination}
					search={search}
					searchThreshold={0}
					setCheckedIds={setCheckedIds}
					sort={sort}
					toggleAddModal={toggleCreateSetupModalModal}
					toggleDeleteModal={openDeleteConfirm}
				/>
			</div>
			<Modal
				isOpen={isBrandingSetupModalOpen}
				onClose={toggleBrandingSetupModal}
				title={isEditMode ? 'Edit setup' : 'Add new setup'}
				width='680px'
			>
				<BrandingSetupForm
					closeModals={closeModals}
					editableSetup={editableSetup}
					isEditMode={isEditMode}
					onClose={toggleBrandingSetupModal}
					setIsDirty={setIsDirty}
				/>
			</Modal>
			<Modal
				isOpen={isShowDeleteConfirm}
				onClose={closeDeleteConfirm}
				title='Confirm action'
				width={MODAL_WIDTH_480}
			>
				<Confirm
					isLoading={isLoadingDeleteBrandingSetup || isLoadingDeleteBrandingSetups}
					onSubmit={deleteItemsHandler}
					question='Are you sure you want to delete the setup(s)?'
				/>
			</Modal>
			<Modal
				isOpen={isCloseConfirmModal}
				onClose={toggleBrandingSetupModal}
				title='Confirm action'
				width={MODAL_WIDTH_480}
			>
				<Confirm
					onSubmit={closeModals}
					question={modalCloseConfirm.question}
				>
					{modalCloseConfirm.children}
				</Confirm>
			</Modal>
		</div>
	);
};

export default memo(ManagePlatformBranding);
